import { treatmentTimeMap } from './treatment-time-map';
import { checkForXSAreas } from './checkForXSAreas';
import { calculateExtensionsTimes } from './calculateExtensionsTimes';

// This function converts an even number to the next smaller odd number
const convertToOdd = (n) => n - ((n + 1) % 2);

// Helper functions
const countAreas = (items, sizesArr) =>
  items.filter((item) => sizesArr.includes(item.size)).length;

// ------------------- Time Adjustment Rules ------------------ //
// These special combinations require adding 10 minutes back (avoiding the standard -10 subtraction)
const getSpecialCombinationAdjustment = (items, xs_count) => {
  const xlOrLgCount = countAreas(items, ['XL', 'LG']);
  if (xlOrLgCount !== 1 || xs_count > 3) return false;
  const smCount = countAreas(items, ['SM']);
  const mdCount = countAreas(items, ['MD']);
  const smOrMdCount = smCount + mdCount;

  // Rule 1: 1 (XL or LG) + 1 (SM or MD) + 2 XS
  const isRule1 = smOrMdCount === 1 && xs_count === 2;

  // Rule 2: (2 or 3) XS + 1 SM + 1 MD + 1 (LG or XL)
  const isRule2 =
    (xs_count === 2 || xs_count === 3) && smCount === 1 && mdCount === 1;

  // Rule 3: 3 XS + 1 (SM or MD) + 1 (LG or XL)
  const isRule3 =
    xs_count === 3 && smCount > 0 !== mdCount > 0 && xlOrLgCount === 1;
  // OR alternative Rule 3 logic
  // const isRule3 = xs_count === 3 && ((smCount > 0 && mdCount === 0) || (smCount === 0 && mdCount > 0)) && xlOrLgCount === 1;

  return isRule1 || isRule2 || isRule3;
};

// Calculate minutes for all XS areas
const calculateAllXSMinutes = (xs_count) => {
  const oddXS = convertToOdd(xs_count);
  return oddXS < 2 ? 10 : oddXS < 4 ? 20 : 30;
};

// Calculate base minutes for mixed areas (non-XS only scenario)
const calculateMixedAreasMinutes = (sortedItems, xs_count) => {
  // Base calculation: subtract 10 from each area's time, starting with 10
  const baseMinutes = sortedItems.reduce((acc, area) => {
    return acc + (treatmentTimeMap[area.size] - 10);
  }, 10);

  // Check if we need to add 10 minutes back for special combinations
  const specialAdjustment = getSpecialCombinationAdjustment(
    sortedItems,
    xs_count
  )
    ? 10
    : 0;

  // XS area additional time calculation
  const hasLargerAreas = countAreas(sortedItems, ['LG', 'XL']) > 0;
  const maxXS = Math.min(xs_count, 5); // Cap xs at 5

  const xsAdditionalMinutes = (() => {
    if (xs_count < 2) return 0;
    if (maxXS >= 4 || (hasLargerAreas && maxXS >= 2)) return 10 * (maxXS - 2);
    return 10 * (maxXS - 1);
  })();

  return baseMinutes + specialAdjustment + xsAdditionalMinutes;
};

export const calculateMinutes = (items, isFirstTreatment) => {
  // Sort items by size (largest first)
  const sortedItems = [...items].sort(
    (a, b) => treatmentTimeMap[b.size] - treatmentTimeMap[a.size]
  );

  const { adjusted_xs_areas_count, are_all_items_xs } =
    checkForXSAreas(sortedItems);
  const xs = adjusted_xs_areas_count;

  // Calculate base minutes
  let minutes = are_all_items_xs
    ? calculateAllXSMinutes(xs)
    : calculateMixedAreasMinutes(sortedItems, xs);

  // Cap at 120 minutes before extensions
  minutes = Math.min(minutes, 120);

  // Add extensions
  const { extensionsTimes, extensionsTimesTotal } = calculateExtensionsTimes(
    items,
    minutes,
    isFirstTreatment
  );

  return {
    minutes: minutes + extensionsTimesTotal,
    extensionsTimes,
    extensionsTimesTotal,
  };
};
